import { useMemo, useState } from "react";
import { Box, Typography, Stack, TextField, InputAdornment, Checkbox, Chip, Button, Alert, CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { generateButtonSx } from "../../../../utils/elementor-style-override";
import useUsersMfa from "./hooks/useUsersMfa";
import useSetUsersMFA from "./hooks/useSetUsersMFA";

const primaryBlue = "#1D54A2";

const saveButtonSx = {
	backgroundColor: `${primaryBlue} !important`,
	color: "#FFFFFF !important",
	fontWeight: 700,
	...generateButtonSx({ bg: "#16407F !important", text: "#FFFFFF" }, { bg: "#16407F !important", text: "#FFFFFF" }),
};

const discardButtonSx = {
	backgroundColor: "#F4F4F5 !important",
	color: `${primaryBlue} !important`,
	...generateButtonSx({ bg: "#E4E4E7 !important", text: primaryBlue }, { bg: "#E4E4E7 !important", text: primaryBlue }),
};

const ROLE_LABELS = {
	administrator: "Admin",
	admin_demo: "Admin Demo",
	ah_agent: "Agent",
	internal_agent: "Internal Agent",
};

/**
 * Admin Settings › MFA Preferences: per-user switch for whether login asks for an authenticator
 * code. Edits are staged locally and only the users that changed are sent on Save.
 */
export default function UsersMFA({ showSnackbar }) {
	const { users, setUsers, globallyEnabled, loading, error } = useUsersMfa();
	const { save, saving } = useSetUsersMFA();
	const [search, setSearch] = useState("");
	// user id → staged enabled state; only holds users whose state differs from what's saved.
	const [changes, setChanges] = useState(() => new Map());

	const filtered = useMemo(() => {
		const term = search.trim().toLowerCase();
		if (!term) return users;
		return users.filter((u) => u.display_name?.toLowerCase().includes(term) || u.user_email?.toLowerCase().includes(term));
	}, [users, search]);

	const isChecked = (user) => (changes.has(user.id) ? changes.get(user.id) : user.mfa_enabled);

	const toggle = (user) => {
		setChanges((prev) => {
			const next = new Map(prev);
			const desired = !(prev.has(user.id) ? prev.get(user.id) : user.mfa_enabled);

			if (desired === user.mfa_enabled) {
				next.delete(user.id);
			} else {
				next.set(user.id, desired);
			}

			return next;
		});
	};

	const handleSave = async () => {
		const result = await save(changes);

		if (!result.success) {
			showSnackbar?.(result.error || "Could not save MFA preferences.", "error");
			return;
		}

		const updatedById = new Map(result.updated.map((u) => [u.id, u.enabled]));
		setUsers((prev) => prev.map((u) => (updatedById.has(u.id) ? { ...u, mfa_enabled: updatedById.get(u.id) } : u)));

		// Keep failed rows staged so the admin can see what didn't go through.
		const failedIds = new Set(result.failed.map((f) => f.id));
		setChanges((prev) => new Map([...prev].filter(([id]) => failedIds.has(id))));

		if (result.failed.length) {
			showSnackbar?.(`${result.updated.length} saved, ${result.failed.length} failed: ${result.failed[0].reason}`, "warning");
		} else {
			showSnackbar?.(`MFA preferences saved for ${result.updated.length} user${result.updated.length === 1 ? "" : "s"}.`, "success");
		}
	};

	return (
		<Box>
			<Typography variant="h5" fontWeight={600} mb={0.5}>
				MFA Preferences
			</Typography>
			<Typography variant="body2" color="text.secondary" mb={2.5}>
				Choose who is asked for an authenticator code when signing in. Unchecked users sign in with their password only.
			</Typography>

			{!globallyEnabled && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Multi-factor authentication is currently turned off site-wide, so nobody is asked for a code. These preferences take effect once
					it's turned back on.
				</Alert>
			)}

			<TextField
				size="small"
				fullWidth
				placeholder="Search by name or email"
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon fontSize="small" />
						</InputAdornment>
					),
				}}
				sx={{ mb: 2 }}
			/>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
					<CircularProgress size={28} />
				</Box>
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : (
				<Box sx={{ border: "1px solid #E8EDF2", borderRadius: "12px", maxHeight: 480, overflowY: "auto" }}>
					{filtered.length === 0 ? (
						<Typography sx={{ p: 3, textAlign: "center", fontSize: 14, color: "text.secondary" }}>No users match that search.</Typography>
					) : (
						filtered.map((user, index) => (
							<Stack
								key={user.id}
								component="label"
								direction="row"
								alignItems="center"
								gap={1.5}
								sx={{
									px: 2,
									py: 1,
									cursor: "pointer",
									borderTop: index === 0 ? "none" : "1px solid #E8EDF2",
									bgcolor: changes.has(user.id) ? "#FFF8E1" : "transparent",
									"&:hover": { bgcolor: changes.has(user.id) ? "#FFF3C4" : "#F5F8FC" },
								}}
							>
								<Checkbox checked={isChecked(user)} onChange={() => toggle(user)} size="small" />
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
										{user.display_name}
									</Typography>
									<Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
										{user.user_email}
									</Typography>
								</Box>
								<Stack direction="row" gap={0.5} sx={{ display: { xs: "none", sm: "flex" } }}>
									{user.roles.map((role) => (
										<Chip key={role} size="small" label={ROLE_LABELS[role] || role} />
									))}
								</Stack>
							</Stack>
						))
					)}
				</Box>
			)}

			<Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1.5} mt={2.5}>
				{changes.size > 0 && (
					<Typography sx={{ fontSize: 13, color: "text.secondary", mr: "auto" }}>
						{changes.size} unsaved change{changes.size === 1 ? "" : "s"}
					</Typography>
				)}
				<Button variant="contained" onClick={() => setChanges(new Map())} disabled={!changes.size || saving} sx={discardButtonSx}>
					Discard
				</Button>
				<Button
					variant="contained"
					startIcon={<SaveOutlinedIcon />}
					onClick={handleSave}
					disabled={!changes.size || saving}
					sx={saveButtonSx}
				>
					{saving ? "Saving..." : "Save"}
				</Button>
			</Stack>
		</Box>
	);
}
